import { Flex } from '@radix-ui/themes'
import { useSetAtom } from 'jotai'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  data,
  type HeadersFunction,
  Links,
  type LinksFunction,
  type LoaderFunctionArgs,
  Meta,
  type MetaFunction,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useLocation,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { Navigation } from './components/main/navigation.tsx'
import Overlays from './components/main/overlays.tsx'
import { ChangelogDialog } from './components/ui/changelog-dialog.tsx'
import { EnvironmentBanner } from './components/ui/environment-banner.tsx'
import { GeneralErrorBoundary } from './components/ui/error-boundary.tsx'
import { InfoBanner } from './components/ui/info-banner.tsx'
import { EpicProgress } from './components/ui/progress-bar.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import { useToast } from './components/ui/toaster.tsx'
import { DexTheme } from './contexts/dex-theme.tsx'
import {
  type ChangelogVersion,
  getChangelog,
  getUnseenVersions,
} from './data/changelog.server.ts'
import { getActiveBanners } from './data/info-banner.server.ts'
import { globalWorkspaceAtom } from './data/states/global-state.ts'
import { sanitizeUserForClient } from './data/user.server.ts'
import { getWorkspacesForUser } from './data/workspace.server.ts'
import useOverlays from './hooks/overlays.hook.ts'
import { useTopChromeOffset } from './hooks/top-chrome-offset.hook.ts'
import { useTheme } from './routes/_api+/api.theme-switch.tsx'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import {
  getSessionAuthMethod,
  getUserId,
  logout,
  usesTuniAuth,
} from './utils/auth.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { type AppEnv, getEnv, getSupportContacts } from './utils/env.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { combineHeaders, getDomainUrl } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { getTheme, type Theme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'

export const links: LinksFunction = () => {
  return [
    {
      rel: 'alternate icon',
      type: 'image/png',
      href: '/favicons/favicon-32x32.png',
    },
    { rel: 'apple-touch-icon', href: '/favicons/apple-touch-icon.png' },
    {
      rel: 'manifest',
      href: '/site.webmanifest',
      crossOrigin: 'use-credentials',
    } as const, // necessary to make typescript happy
    { rel: 'stylesheet', href: tailwindStyleSheetUrl },
  ].filter(Boolean)
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const appEnv = data?.ENV?.APP_ENV
  const title = appEnv === 'labs' ? 'DEx Labs' : 'DEx'
  return [
    { title: data ? title : 'Error | DEx' },
    { name: 'description', content: 'Design Explorer' },
  ]
}

export async function loader({ request }: LoaderFunctionArgs) {
  const timings = makeTimings('root loader')
  const userId = await time(() => getUserId(request), {
    timings,
    type: 'getUserId',
    desc: 'getUserId in root',
  })

  const user = userId
    ? await time(
        () =>
          prisma.user.findUniqueOrThrow({
            select: {
              id: true,
              name: true,
              username: true,
              email: true,
              lastSeenVersion: true,
              roles: {
                select: {
                  name: true,
                },
              },
            },
            where: { id: userId },
          }),
        { timings, type: 'find user', desc: 'find user in root' },
      )
    : null
  if (userId && !user) {
    console.info('something weird happened')
    // something weird happened... The user is authenticated but we can't find
    // them in the database. Maybe they were deleted? Let's log them out.
    await logout({ request, redirectTo: '/' })
  }
  const { toast, headers: toastHeaders } = await getToast(request)
  const honeyProps = honeypot.getInputProps()
  const authMethod = userId ? await getSessionAuthMethod(request) : null

  // Get user's workspaces and determine their default workspace
  const userWorkspaces = user ? await getWorkspacesForUser(user.id) : []
  // User's default workspace is the one marked as default (if they have access) or their first workspace
  const userDefaultWorkspace =
    userWorkspaces.find(w => w.isDefault) || userWorkspaces[0] || null

  // When the active session uses TUNI auth, username contains the opaque
  // SHIB_eppn identifier — strip it so it never reaches the browser.
  const sanitizedUser =
    user && (await usesTuniAuth(request)) ? sanitizeUserForClient(user) : user

  // Get active info banners for all users (global, not workspace-scoped)
  const activeBanners = await time(() => getActiveBanners(), {
    timings,
    type: 'get active banners',
    desc: 'get active banners in root',
  })

  // Determine unseen changelog versions for the logged-in user
  const unseenChangelog: ChangelogVersion[] = user
    ? getUnseenVersions(user.lastSeenVersion)
    : []
  const allChangelog = getChangelog().versions

  return data(
    {
      user: sanitizedUser,
      authMethod,
      defaultWorkspace: userDefaultWorkspace,
      userWorkspaces,
      activeBanners,
      unseenChangelog,
      allChangelog,
      requestInfo: {
        hints: getHints(request),
        origin: getDomainUrl(request),
        path: new URL(request.url).pathname,
        userPrefs: {
          theme: getTheme(request),
        },
      },
      ENV: getEnv(),
      supportContacts: getSupportContacts(),
      toast,
      honeyProps,
    },
    {
      headers: combineHeaders(
        { 'Server-Timing': timings.toString() },
        toastHeaders,
      ),
    },
  )
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
  const headers = {
    'Server-Timing': loaderHeaders.get('Server-Timing') ?? '',
  }
  return headers
}

function Document({
  children,
  nonce,
  theme = 'light',
  env = {},
}: {
  children: React.ReactNode
  nonce: string
  theme?: Theme
  env?: Record<string, string | undefined>
}) {
  return (
    <html lang="en" className={`${theme} h-full`}>
      <head>
        <ClientHintCheck nonce={nonce} />
        <Meta />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <meta name="robots" content="noindex, nofollow" />
        <Links />
      </head>

      <body className="flex h-full flex-col overflow-hidden">
        <DexTheme theme={theme}>
          {children}

          <script
            nonce={nonce}
            dangerouslySetInnerHTML={{
              __html: `window.ENV = ${JSON.stringify(env)}`,
            }}
          />
          <ScrollRestoration nonce={nonce} />
          <Scripts nonce={nonce} />
        </DexTheme>
        <EpicToaster closeButton position="top-center" theme={theme} />
      </body>
    </html>
  )
}

function App() {
  const data = useLoaderData<typeof loader>()
  const nonce = useNonce()
  const theme = useTheme()
  const { removeOverlays } = useOverlays()
  const location = useLocation()
  const appEnv = (data.ENV.APP_ENV || 'production') as AppEnv
  const setWorkspace = useSetAtom(globalWorkspaceAtom)
  const topChromeRef = useRef<HTMLDivElement>(null)

  // Changelog dialog state
  const hasUnseenChanges = data.unseenChangelog.length > 0
  const [showChangelogPopup, setShowChangelogPopup] = useState(false)
  const [showReleaseNotes, setShowReleaseNotes] = useState(false)

  // Open popup when unseen changes appear (e.g. after login redirect)
  useEffect(() => {
    if (hasUnseenChanges) {
      setShowChangelogPopup(true)
    }
  }, [hasUnseenChanges])

  const handleOpenReleaseNotes = useCallback(() => {
    setShowReleaseNotes(true)
  }, [])

  useToast(data.toast)

  // Clear workspace from global state on logout
  useEffect(() => {
    if (!data.user) {
      setWorkspace(null)
    }
  }, [data.user, setWorkspace])

  useEffect(() => {
    return () => {
      removeOverlays({})
    }
  }, [location])

  useTopChromeOffset(topChromeRef, [appEnv, data.userWorkspaces.length])

  return (
    <Document nonce={nonce} theme={theme} env={data.ENV}>
      <div ref={topChromeRef} data-app-chrome="top">
        <InfoBanner banners={data.activeBanners} />
        <EnvironmentBanner appEnv={appEnv} />
        <Navigation
          appEnv={appEnv}
          userPrefsTheme={data.requestInfo.userPrefs.theme ?? undefined}
          userWorkspaces={data.userWorkspaces}
          supportContacts={data.supportContacts}
          onOpenReleaseNotes={
            data.allChangelog.length > 0 ? handleOpenReleaseNotes : undefined
          }
        />
      </div>

      <Flex
        flexGrow="1"
        direction="column"
        className="font-poppins min-h-0 w-screen overflow-y-auto bg-app px-6 pb-10 pt-4 text-foreground"
      >
        <Outlet />
      </Flex>

      {/* Auto-popup for unseen changelog versions */}
      <ChangelogDialog
        versions={data.unseenChangelog}
        open={showChangelogPopup}
        onOpenChange={setShowChangelogPopup}
        dismissUpdatesLastSeen
      />

      {/* Manual "Release notes" from UserDropdown */}
      <ChangelogDialog
        versions={data.allChangelog}
        open={showReleaseNotes}
        onOpenChange={setShowReleaseNotes}
      />

      <Overlays />
      <EpicProgress />
    </Document>
  )
}

function AppWithProviders() {
  const data = useLoaderData<typeof loader>()
  return (
    <HoneypotProvider {...data.honeyProps}>
      <App />
    </HoneypotProvider>
  )
}

export default AppWithProviders

export function ErrorBoundary() {
  // the nonce doesn't rely on the loader so we can access that
  const nonce = useNonce()

  // NOTE: you cannot use useLoaderData in an ErrorBoundary because the loader
  // likely failed to run so we have to do the best we can.
  // We could probably do better than this (it's possible the loader did run).
  // This would require a change in Remix.

  // Just make sure your root route never errors out and you'll always be able
  // to give the user a better UX.

  // Best-effort: read APP_ENV from window.ENV if the root loader managed to
  // inject it before the error occurred.
  const appEnv =
    typeof document !== 'undefined'
      ? ((window.ENV?.APP_ENV || 'production') as AppEnv)
      : ('production' as AppEnv)

  return (
    <Document nonce={nonce}>
      <EnvironmentBanner appEnv={appEnv} />
      <GeneralErrorBoundary />
    </Document>
  )
}
